import { motion } from "framer-motion";

/* Browser chrome frame wrapper */
export const BrowserFrame = ({ children, url = "", className = "" }) => (
  <div className={`rounded-xl overflow-hidden border border-white/[0.08] bg-[#1C1C1C] shadow-[0_8px_40px_rgba(0,0,0,0.5)] ${className}`}>
    <div className="flex items-center gap-2 px-4 py-2.5 bg-[#161616] border-b border-white/[0.06]">
      <div className="flex gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-white/[0.12]" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/[0.12]" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/[0.12]" />
      </div>
      {url && (
        <div className="flex-1 mx-4">
          <div className="bg-white/[0.05] rounded-md px-3 py-1 text-[10px] font-mono text-[#A1A1AA]/60 truncate max-w-md">
            {url}
          </div>
        </div>
      )}
    </div>
    <div className="overflow-hidden">{children}</div>
  </div>
);

/* Hero: single large featured screenshot in browser frame */
export const HeroScreenshot = ({ src, alt, url, caption }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    className="mb-6"
    data-testid="hero-screenshot"
  >
    <BrowserFrame url={url}>
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto block" />
    </BrowserFrame>
    {caption && (
      <p className="text-xs font-mono text-[#A1A1AA]/50 mt-3 pl-1">{caption}</p>
    )}
  </motion.div>
);

/* Bento: 1 large left + 2 stacked right */
export const BentoLayout = ({ images }) => {
  if (!images || images.length < 2) return null;
  const [hero, ...rest] = images;
  const secondary = rest.slice(0, 2);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6" data-testid="bento-layout">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="lg:col-span-3"
      >
        <BrowserFrame url={hero.url}>
          <img src={hero.src} alt={hero.alt} loading="lazy" className="w-full h-auto block" />
        </BrowserFrame>
        {hero.caption && <p className="text-xs font-mono text-[#A1A1AA]/50 mt-2 pl-1">{hero.caption}</p>}
      </motion.div>
      <div className="lg:col-span-2 flex flex-col gap-4">
        {secondary.map((img, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="flex-1"
          >
            <div className="screenshot-container h-full">
              <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-cover block" />
            </div>
            {img.caption && <p className="text-xs font-mono text-[#A1A1AA]/50 mt-2 pl-1">{img.caption}</p>}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/* Asymmetric: alternating large/small in a masonry-ish feel */
export const AsymmetricGrid = ({ images }) => {
  if (!images || images.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6" data-testid="asymmetric-grid">
      {images.map((img, i) => {
        // Alternate: wide (8-col), narrow (4-col), then swap
        const isWide = i % 3 === 0;
        const isMedium = i % 3 === 1;
        const span = isWide ? "md:col-span-8" : isMedium ? "md:col-span-4" : "md:col-span-6";

        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className={`${span}`}
          >
            <div className="screenshot-container">
              <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-auto block" />
            </div>
            {img.caption && <p className="text-xs font-mono text-[#A1A1AA]/50 mt-2 pl-1">{img.caption}</p>}
          </motion.div>
        );
      })}
    </div>
  );
};

/* Stacked pair: two screenshots side by side, equal */
export const ScreenshotPair = ({ images }) => {
  if (!images || images.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="screenshot-pair">
      {images.map((img, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <BrowserFrame url={img.url}>
            <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-auto block" />
          </BrowserFrame>
          {img.caption && <p className="text-xs font-mono text-[#A1A1AA]/50 mt-2 pl-1">{img.caption}</p>}
        </motion.div>
      ))}
    </div>
  );
};
